import { Component, inject, OnInit, HostListener } from '@angular/core';
import { trigger, style, animate, transition } from '@angular/animations';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import {
  ExamInfo,
  Package,
  UserPlanResponse,
} from '../../shared/interfaces/reserve-loading.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { UserPlanRequest } from '../../shared/interfaces/reserve-health.model';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { NumberCommaPipe } from '../../pipes/number-comma.pipe';
import { NONE_OF_FOLLOWING } from '../../shared/constants/health-questionnaire-fields.constants';
import { NgOptimizedImage } from '@angular/common';
import { Decimal } from 'decimal.js';
import { AppointmentDiscountRateService } from '../../shared/services/appointment-discountRate.service';

/**
 * 專屬方案結果頁面
 */
@Component({
  selector: 'app-reserve-result',
  standalone: true,
  imports: [
    TranslatePipe,
    MatButton,
    MatIconButton,
    NumberCommaPipe,
    NgOptimizedImage,
  ],
  templateUrl: './reserve-result.component.html',
  styleUrls: ['./reserve-result.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition('* => *', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class ReserveResultComponent implements OnInit {
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  translateService: TranslateService = inject(TranslateService);
  dialog: MatDialog = inject(MatDialog);
  router: Router = inject(Router);
  appointmentDiscountRateService: AppointmentDiscountRateService = inject(AppointmentDiscountRateService);
  partyDiscountRate: string = ''; // 活動折扣率

  allCheckupDiscountRate: string = ''; // 全方位方案折扣率
  aiExclusiveDiscountRate: string = ''; // 智能專屬方案折扣率
  basicCheckupDiscountRate: string = ''; // 基礎方案折扣率

  userPlanResponse?: UserPlanResponse;

  // 方案底下所有的健檢項目(全方位/智能專屬/安心起步)
  examInfoGroupByPackage: Array<Array<ExamInfo>> = [[], [], []];

  // 所有風險因子
  allRiskItem: Array<string> = [];

  // 每個類別的examInfo是否顯示全部或收合
  isExamInfoGroupByTypeShowAll: Array<boolean> = [];

  ngOnInit() {
    // 將當前預約步驟設置為3
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 3);
    });

    // 取得活動折扣率
    const userPlan: UserPlanRequest | null =
      this.sharedStorageService.get('userPlanRequest') ?? null;
    if (userPlan?.orgNo) {
      this.appointmentDiscountRateService.getDiscountRates(userPlan.orgNo).subscribe(res => {
        console.log("折扣率:", res.data);
        if (res.data?.discountRate) {
          console.log("partyDiscountRate:", res.data.discountRate);
          this.partyDiscountRate = res.data.discountRate;
          this.sharedStorageService.set('partyDiscountRate', this.partyDiscountRate);
        }
      });
    }

    // 取得上一頁reserve-loading請求的user plan response
    this.userPlanResponse =
      this.sharedStorageService.get('userPlanResponse') ?? undefined;

    // 把所有在本方案內的檢項 添加至全方位方案
    this.userPlanResponse?.allCheckup.packageList.forEach(packageList => {
      const deptName = packageList.deptName;
      packageList.examItemList.forEach(examInfo => {
        examInfo.deptName = deptName;
        this.examInfoGroupByPackage[0].push(examInfo);
      });
    });

    // 把所有在本方案內的檢項 添加至智能專屬方案
    this.userPlanResponse?.aiExclusive.packageList.forEach(packageList => {
      const deptName = packageList.deptName;
      packageList.examItemList.forEach(examInfo => {
        examInfo.deptName = deptName;
        this.examInfoGroupByPackage[1].push(examInfo);
      });
    });

    // 把所有在本方案內的檢項 添加至安心起步方案
    this.userPlanResponse?.basicCheckup.packageList.forEach(packageList => {
      const deptName = packageList.deptName;
      packageList.examItemList.forEach(examInfo => {
        examInfo.deptName = deptName;
        this.examInfoGroupByPackage[2].push(examInfo);
      });
    });

    // 取得所有風險因子(為健康調查表用戶勾選之項目)
    const userPlanRequest: UserPlanRequest | null =
      this.sharedStorageService.get('userPlanRequest') ?? null;
    const riskSet = new Set<string>();
    [
      ...(userPlanRequest?.history ?? []),
      ...(userPlanRequest?.family ?? []),
      ...(userPlanRequest?.subjectiveSymptoms ?? []),
    ].forEach(item => {
      const parts = item.split('_');
      if (parts[1] && parts[1] !== NONE_OF_FOLLOWING) {
        riskSet.add(parts[1]);
      }
    });
    this.allRiskItem = Array.from(riskSet);

    // 初始化每個類別的examInfo收合判斷
    this.isExamInfoGroupByTypeShowAll = Array(
      this.allExamInfoGroupByType.size
    ).fill(false);
  }

  // 取得聚焦評估症狀
  get riskItemsText(): string {
    if (!this.userPlanResponse?.riskItems) return '';

    const andText = this.translateService.instant(
      'reserve-result.risk-hint-and'
    );
    const items = this.userPlanResponse.riskItems;
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return items.join(andText);
    return `<span class="text-highlight">${items.slice(0, -1).join('、')}</span> ${andText} <span class="text-highlight">${items[items.length - 1]}</span>`;
  }

  // 取得"全方位健檢"方案
  get allCheckup(): Package | undefined {
    return this.userPlanResponse?.allCheckup;
  }

  // 取得"智能專屬"方案
  get aiExclusive(): Package | undefined {
    return this.userPlanResponse?.aiExclusive;
  }

  // 取得"基礎健檢"方案
  get basicCheckup(): Package | undefined {
    return this.userPlanResponse?.basicCheckup;
  }

  // 所有examInfo根據examType分組
  get allExamInfoGroupByType(): Map<string, ExamInfo[]> {
  const allExamInfo: ExamInfo[] = [
    ...(this.allCheckup?.packageList ?? []),
    ...(this.aiExclusive?.packageList ?? []),
    ...(this.basicCheckup?.packageList ?? []),
  ].flatMap(pkg =>
    pkg.examItemList.map(exam => ({
      ...exam,
      deptName: pkg.deptName,
    }))
  );

  const result = new Map<string, ExamInfo[]>();
  allExamInfo.forEach(exam => {
    const type = exam.examType;
    if (!type) return;
    if (!result.has(type)) {
      result.set(type, []);
    }
    const list = result.get(type)!;
    if (!list.some(e => e.examItem === exam.examItem)) {
      list.push(exam);
    }
  });

  // 加入排序邏輯
  const order = ['潛在疾病風險檢查建議', '個人歷史異常追蹤', '優先必要檢查項目'];
  const sortedEntries = Array.from(result.entries()).sort(([a], [b]) => {
    const indexA = order.indexOf(a);
    const indexB = order.indexOf(b);
    
    // 如果某個類型不在預定義順序中，放到最後
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    
    return indexA - indexB;
  });
  
  return new Map(sortedEntries);
}

  // examInfo對全方位健檢是否checked
  isAllCheckupChecked(examInfo: ExamInfo): boolean {
    return this.examInfoGroupByPackage[0].some(
      e => e.examItem === examInfo.examItem
    );
  }

  // examInfo對智能專屬是否checked
  isAiExclusiveChecked(examInfo: ExamInfo): boolean {
    return this.examInfoGroupByPackage[1].some(
      e => e.examItem === examInfo.examItem
    );
  }

  // examInfo對基礎健檢是否checked
  isBasicCheckupChecked(examInfo: ExamInfo): boolean {
    return this.examInfoGroupByPackage[2].some(
      e => e.examItem === examInfo.examItem
    );
  }

  // 取得全方位檢項數量 index 0: 潛在疾病 1: 個人異常追蹤 2: 標準檢查
  getAllCheckupCountByExamTypeIndex(index: number): number {
  const examTypeArray = Array.from(this.allExamInfoGroupByType.keys());
  const examType = examTypeArray[index];
  
  switch (examType) {
    case '潛在疾病風險檢查建議':
      return Number(this.allCheckup?.potentialCnt ?? 0);
    case '個人歷史異常追蹤':
      return Number(this.allCheckup?.abnormalCnt ?? 0);
    case '優先必要檢查項目':
      return 0;
    default:
      return 0;
  }
}

  // 取得智能專屬檢項數量 index 0: 潛在疾病 1: 個人異常追蹤 2: 標準檢查
  getAiExclusiveCountByExamTypeIndex(index: number): number {
  const examTypeArray = Array.from(this.allExamInfoGroupByType.keys());
  const examType = examTypeArray[index];
  
  switch (examType) {
    case '潛在疾病風險檢查建議':
      return Number(this.aiExclusive?.potentialCnt ?? 0);
    case '個人歷史異常追蹤':
      return Number(this.aiExclusive?.abnormalCnt ?? 0);
    case '優先必要檢查項目':
      return 0;
    default:
      return 0;
  }
}

  // 取得基礎檢項數量 index 0: 潛在疾病 1: 個人異常追蹤 2: 標準檢查
  getBasicCheckupCountByExamTypeIndex(index: number): number {
  const examTypeArray = Array.from(this.allExamInfoGroupByType.keys());
  const examType = examTypeArray[index];
  
  switch (examType) {
    case '潛在疾病風險檢查建議':
      return Number(this.basicCheckup?.potentialCnt ?? 0);
    case '個人歷史異常追蹤':
      return Number(this.basicCheckup?.abnormalCnt ?? 0);
    case '優先必要檢查項目':
      return 0;
    default:
      return 0;
  }
}

  // 開啟/收合examInfo
  toggleExamInfoGroupByTypeShowAll(index: number) {
    this.isExamInfoGroupByTypeShowAll[index] =
      !this.isExamInfoGroupByTypeShowAll[index];
  }

  // 手機版展示檢項dialog
  showExamDialog(examInfo: ExamInfo) {
    const examDialog = this.dialog.open(SingleActionDialogComponent, {
      data: {
        title: examInfo.examName,
        message: examInfo.examMemo,
        buttonText: this.translateService.instant('common.button.i-understood'),
        showCloseButton: false,
        action: () => examDialog.close(),
      },
    });
  }

  /**
   * 選擇方案
   * @param type 0: 全方位 1: 智能專屬 2: 基礎
   */
  choosePackage(type: number) {
    if (type === 0) {
      this.sharedStorageService.set(
        'selectedPackage',
        this.translateService.instant('reserve-result.all-checkup')
      );
      this.sharedStorageService.set(
        'examItemList',
        this.examInfoGroupByPackage[0]
      );
      this.sharedStorageService.set(
        'packagePrice',
        this.calculatedAllCheckupPrice.toString()
      );
      this.sharedStorageService.set('originalPrice', this.allCheckUpOriginalPrice);
      this.allCheckupDiscountRate = this.allCheckup?.discountRate ?? '1';
      this.sharedStorageService.set(
        'discountRate',
        this.allCheckupDiscountRate
      );
    } else if (type === 1) {
      this.sharedStorageService.set(
        'selectedPackage',
        this.translateService.instant('reserve-result.ai-exclusive')
      );
      this.sharedStorageService.set(
        'examItemList',
        this.examInfoGroupByPackage[1]
      );
      this.sharedStorageService.set(
        'packagePrice',
        this.calculatedAiExclusivePrice.toString()
      );
      this.sharedStorageService.set('originalPrice', this.aiExclusiveOriginalPrice);
      this.aiExclusiveDiscountRate = this.aiExclusive?.discountRate ?? '1';
      this.sharedStorageService.set(
        'discountRate',
        this.aiExclusiveDiscountRate
      );
    } else {
      this.sharedStorageService.set(
        'selectedPackage',
        this.translateService.instant('reserve-result.basic-checkup')
      );
      this.sharedStorageService.set(
        'examItemList',
        this.examInfoGroupByPackage[2]
      );
      this.sharedStorageService.set(
        'packagePrice',
        this.calculatedBasicCheckupPrice.toString()
      );
      this.sharedStorageService.set('originalPrice', this.basicCheckupOriginalPrice);
      this.basicCheckupDiscountRate = this.basicCheckup?.discountRate ?? '1';
      this.sharedStorageService.set(
        'discountRate',
        this.basicCheckupDiscountRate
      );
    }
    this.router.navigate(['/healthcheck/reserve/additem']).then();
  }

  protected readonly Number = Number;

  //滾動對應差異方案 出現固定方案
  currentTableHeaderTitle: string = '';
  isTableFixedActive: boolean = false;
  animationKey = 0;

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const reserveTableBlock = document.querySelector(
      '.reserve-table-block'
    ) as HTMLElement;
    const tableHeaders = this.getTableHeaders();
    let activeTitle = '';

    if (reserveTableBlock) {
      const blockPosition = reserveTableBlock.getBoundingClientRect();
      const offset = window.innerWidth <= 768 ? -24 : -24;
      this.isTableFixedActive =
        blockPosition.top <= offset && blockPosition.bottom > offset;
    }

    tableHeaders.forEach((header: HTMLElement) => {
      const headerPosition = header.getBoundingClientRect();
      const offset = window.innerWidth <= 768 ? 176 : 162;
      if (headerPosition.top <= offset && headerPosition.bottom > offset) {
        const titleElement = header.querySelector(
          '.plan-table-header-title'
        ) as HTMLElement;
        activeTitle = titleElement?.textContent?.trim() || '';
      }
    });

    // 加 key 值来觸發動畫
    if (this.currentTableHeaderTitle !== activeTitle) {
      this.currentTableHeaderTitle = activeTitle;
      this.animationKey++; //
    }
  }

  private getTableHeaders(): HTMLElement[] {
    return Array.from(
      document.querySelectorAll('.plan-table')
    ) as HTMLElement[];
  }

  //風險因子橫向拖曳
  isDragging = false;
  startX = 0;
  scrollLeft = 0;

  onMouseDown(event: MouseEvent): void {
    const scrollElement = event.target as HTMLElement;
    this.isDragging = true;
    this.startX = event.pageX - scrollElement.offsetLeft;
    this.scrollLeft = scrollElement.scrollLeft;

    scrollElement.addEventListener('mousemove', this.onMouseMove.bind(this));
    scrollElement.addEventListener('mouseup', this.onMouseUp.bind(this));
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    const scrollElement = event.target as HTMLElement;
    const x = event.pageX - scrollElement.offsetLeft;
    const walk = (x - this.startX) * 2; // 調整滾動速度
    scrollElement.scrollLeft = this.scrollLeft - walk;
  }

  onMouseUp(event: MouseEvent): void {
    const scrollElement = event.target as HTMLElement;
    this.isDragging = false;
    scrollElement.removeEventListener('mousemove', this.onMouseMove.bind(this));
    scrollElement.removeEventListener('mouseup', this.onMouseUp.bind(this));
  }

  /**
   * 計算方案總價格（每個檢項先套用折扣並無條件十進位，再加總）
   */
  private calculatePackageTotalPrice(packageData: Package): number {
    let discountRate = Number(packageData.discountRate) || 1;
    // if (packageData == this.allCheckup) {
    //   discountRate = Number(this.allCheckupDiscountRate);
    // } else if (packageData == this.aiExclusive) {
    //   discountRate = Number(this.aiExclusiveDiscountRate);
    // } else if (packageData == this.basicCheckup) {
    //   discountRate = Number(this.basicCheckupDiscountRate);
    // } else {
    //   discountRate = 0;
    // }

    return packageData.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        
        // 檢查 rateStatus，為 true 才套用折扣
        if (examInfo.rateStatus === true) {
          let discountedPrice = originalPrice.mul(discountRate);
          let roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件進位到十位數
          // 若有活動折扣率
          if (this.partyDiscountRate) {
            roundedPrice = roundedPrice.mul(new Decimal(this.partyDiscountRate));
            // 四捨五入到個位整數
            roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
          }
          return sum + roundedPrice.toNumber();
        } else {
          // rateStatus 為 false 時直接加原價
          return sum + originalPrice.toNumber();
        }
      }, 0);
      return total + packageTotal;
    }, 0);
  }

  // 取得全方位方案價格
  get calculatedAllCheckupPrice(): number {
    return this.allCheckup
      ? this.calculatePackageTotalPrice(this.allCheckup)
      : 0;
  }

  // 取得智能專屬方案價格
  get calculatedAiExclusivePrice(): number {
    return this.aiExclusive
      ? this.calculatePackageTotalPrice(this.aiExclusive)
      : 0;
  }

  // 取得基礎方案價格
  get calculatedBasicCheckupPrice(): number {
    return this.basicCheckup
      ? this.calculatePackageTotalPrice(this.basicCheckup)
      : 0;
  }


  // 取得全方位方案原始價格
  get allCheckUpOriginalPrice(): number {
    return this.allCheckup?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0;
  }

  // 取得智能專屬方案原始價格
  get aiExclusiveOriginalPrice(): number {
    return this.aiExclusive?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0;
  }

  // 取得基礎方案原始價格
  get basicCheckupOriginalPrice(): number {
    return this.basicCheckup?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0;
  }
}
